/* eslint-disable function-paren-newline */
import React, { createContext, useContext, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useGrowthBook } from '@growthbook/growthbook-react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchReserhubUser,
  fetchReserhubAccountNextPurchase,
  fetchConnectedAccountByProvider,
  clearAllReserhubData,
} from '@/actions/reserhubAccount';
import getPurchaseStepName from 'utils/purchase/getPurchaseStepName';
import { setAlert } from '@/actions';
import usePurchase from 'hooks/store/usePurchase';
import useWhitelabelEnvs from 'hooks/whitelabel/useWhitelabelEnvs';
import useGrowthBookFeatureValue from 'components/GrowthBookProvider/useGrowthBookFeatureValue';
import isReserhubUnifiedAccountEnabled from 'utils/isReserhubUnifiedAccountEnabled';
import errorMonitoring from 'errorMonitoring';
import { setConfig as setApiConfig } from 'js-api-client';
import reserhubApi from '../services/reserhub-api/reserhubApi';
import { isWebView } from '../utils/webview';
import ReserhubDotersUnavailableDialog from '../components/reserhub/ReserhubDotersUnavailableDialog';

const ReserhubAuthContext = createContext();
const RESERHUB_LOGIN_FLAG = 'RESERHUB_USER_LOGGED_IN';

/**
 * Provider for the ReserhubAuthContext
 * @param {object} props - children.
 * @param {React.ReactNode} props.children - The child components.
 * @returns {React.ReactElement}
 */
export function ReserhubAccountProvider({ children }) {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.reserhubAccount.get('user'));
  const nextPurchase = useSelector((state) => state.reserhubAccount.get('nextPurchase'));
  const isNextPurchaseLoading = useSelector((state) =>
    state.reserhubAccount.get('isNextPurchaseLoading'),
  );
  const isNextPurchaseFetched = useSelector((state) =>
    state.reserhubAccount.get('isNextPurchaseFetched'),
  );
  const whitelabelEnvs = useWhitelabelEnvs();
  const { account } = whitelabelEnvs.reserhub || {};
  // GrowthBook: unified_account re-runs effects after features load when progressiveRollout is on
  const unifiedAccountFeature = useGrowthBookFeatureValue('unified_account');
  const purchase = usePurchase();
  const isUserLoading = useSelector((state) => state.reserhubAccount.get('isUserLoading'));
  const isUserFetched = useSelector((state) => state.reserhubAccount.get('isUserFetched'));
  const connectedAccounts = useSelector((state) => state.reserhubAccount.get('connectedAccounts'));
  const isConnectedAccountsFetched = useSelector((state) =>
    state.reserhubAccount.get('isConnectedAccountsFetched'),
  );
  const isConnectedAccountsLoading = useSelector((state) =>
    state.reserhubAccount.get('isConnectedAccountsLoading'),
  );
  const showDotersUnavailableDialog = useSelector((state) =>
    state.reserhubAccount.get('showDotersUnavailableDialog'),
  );
  const features = useSelector((state) => state.whitelabelConfig?.features ?? {});
  const location = useLocation();
  const pathname = location.pathname ?? '';
  const userFetched = useRef(false);
  const purchaseState = useRef(null);
  const hasFetchedDotersOnSearch = useRef(false);
  const growthBook = useGrowthBook();

  const isPurchasePath = pathname.includes('purchase');
  const accountEnabled =
    !isWebView() &&
    isReserhubUnifiedAccountEnabled(account, {
      unifiedAccountFeature,
    });

  const hasShownSessionExpiredAlert = useRef(false);

  /**
   * Set the login flag in localStorage
   */
  const setLoginFlag = useCallback(() => {
    localStorage.setItem(RESERHUB_LOGIN_FLAG, 'true');
  }, []);

  /**
   * Remove the login flag from localStorage
   */
  const removeLoginFlag = useCallback(() => {
    localStorage.removeItem(RESERHUB_LOGIN_FLAG);
  }, []);

  /**
   * Check if user was previously logged in
   * @returns {boolean} Whether the user was previously logged in
   */
  const wasUserLoggedIn = useCallback(() => {
    return localStorage.getItem(RESERHUB_LOGIN_FLAG) === 'true';
  }, []);

  /**
   * Show session expired alert
   */
  const showSessionExpiredAlert = useCallback(() => {
    if (!hasShownSessionExpiredAlert.current) {
      hasShownSessionExpiredAlert.current = true;
      dispatch(setAlert('reserhub_session_finished', 'warning'));
    }
  }, [dispatch, hasShownSessionExpiredAlert]);

  /**
   * Fetch user with session handling
   */
  const fetchUserWithSessionHandling = useCallback(async () => {
    try {
      const fetchedUser = await dispatch(fetchReserhubUser());

      if (fetchedUser && fetchedUser.id) {
        // User successfully fetched, set login flag
        setLoginFlag();
        hasShownSessionExpiredAlert.current = false;
      } else if (wasUserLoggedIn()) {
        // User was logged in before but fetch failed/returned no user
        showSessionExpiredAlert();
        removeLoginFlag();
      }

      return fetchedUser;
    } catch (error) {
      // If user was previously logged in, show session expired alert
      if (wasUserLoggedIn()) {
        showSessionExpiredAlert();
        removeLoginFlag();
      }
      throw error;
    }
  }, [
    dispatch,
    setLoginFlag,
    hasShownSessionExpiredAlert,
    removeLoginFlag,
    wasUserLoggedIn,
    showSessionExpiredAlert,
  ]);

  /**
   * Logout the user and clear login flag
   */
  async function logout() {
    try {
      const redirectUrl = encodeURIComponent(window.location.href);
      window.location.href = `/auth/logout?redirectUrl=${redirectUrl}`;
      removeLoginFlag();
      hasShownSessionExpiredAlert.current = false;
    } catch (error) {
      // Even if logout fails, remove the flag
      removeLoginFlag();
      hasShownSessionExpiredAlert.current = false;
      throw error;
    }
  }

  /**
   * Login with Doters
   * @returns {Promise<string>} The redirect URL for Doters login
   * @throws Rejects on API failure after surfacing error via setAlert
   */
  async function loginWithDoters() {
    try {
      const { url } = await reserhubApi.loginWithDoters();
      return url;
    } catch (error) {
      errorMonitoring.notify({
        error,
        info: {
          message: 'Error logging in with doters using unified account',
        },
      });
      dispatch(setAlert('reserhub_login_doters_error', 'warning'));
      throw error;
    }
  }

  /**
   * Guard so users out of the experiment do not keep session cookies on API requests.
   * Only after GrowthBook has loaded features (ready); before that unifiedAccountFeature may be stale.
   */
  useEffect(() => {
    if (account?.progressiveRollout !== true || !growthBook?.ready) return;

    if (!accountEnabled) {
      setApiConfig({
        withCredentials: false,
      });
    }
  }, [account, accountEnabled, growthBook?.ready]);

  // Fetch next purchase after user is set
  useEffect(() => {
    if (user && user.id) {
      setLoginFlag();
      hasShownSessionExpiredAlert.current = false;
      if (!isNextPurchaseFetched && !isNextPurchaseLoading) {
        dispatch(fetchReserhubAccountNextPurchase());
      }
    }
  }, [
    user,
    dispatch,
    isNextPurchaseFetched,
    isNextPurchaseLoading,
    setLoginFlag,
    hasShownSessionExpiredAlert,
  ]);

  /**
   * Fetch user if purchase is fetched and the current page is a purchase page.
   * Refresh user if the purchase state is completed and the user is not fetched or the purchase state has changed.
   */
  useEffect(() => {
    if (!accountEnabled) return;

    if (purchase?.state !== 'completed') {
      purchaseState.current = purchase?.state;
    }

    if (
      purchase?.token &&
      isPurchasePath &&
      (!userFetched.current ||
        (purchase?.state !== purchaseState.current && purchase?.state === 'completed'))
    ) {
      const isPurchaseJustCompleted =
        purchase?.state !== purchaseState.current && purchase?.state === 'completed';
      purchaseState.current = purchase?.state;
      userFetched.current = true;
      fetchUserWithSessionHandling();

      if (isPurchaseJustCompleted) {
        dispatch(fetchReserhubAccountNextPurchase());
      }
    }
  }, [
    purchase.token,
    purchase.state,
    accountEnabled,
    isPurchasePath,
    fetchUserWithSessionHandling,
    userFetched,
    dispatch,
  ]);

  /**
   * Fetch user if the current page is not a purchase page and the user is not fetched
   */
  useEffect(() => {
    if (!accountEnabled) return;

    if (!isPurchasePath && !userFetched.current) {
      userFetched.current = true;
      fetchUserWithSessionHandling();
    }
  }, [accountEnabled, isPurchasePath, fetchUserWithSessionHandling, userFetched]);

  /**
   * Refresh connected Doters account when on purchase steps (passengers, seats, checkout)
   * or on search results so balance/points are up to date in the UI.
   */
  useEffect(() => {
    const purchaseStep = getPurchaseStepName(pathname);

    // If is a purchase path the doters account should be updated
    const isPurchaseStepThatNeedsDoters =
      purchaseStep === 'passengers' || purchaseStep === 'checkout' || purchaseStep === 'dSeats';

    // If is a purchase step that needs doters, reset the flag because we are not in search results page
    if (isPurchaseStepThatNeedsDoters) hasFetchedDotersOnSearch.current = false;

    // If is a search results path the doters account should be updated if it hasn't been fetched yet at search page
    const isSearchResultsPath = pathname.includes('/search/') && !hasFetchedDotersOnSearch.current;

    // If is a purchase step or a search results path and the doters account should be updated
    const shouldRefreshDoters =
      accountEnabled &&
      user?.id &&
      features.DOTERS_ENABLED &&
      (isPurchaseStepThatNeedsDoters || isSearchResultsPath);

    if (shouldRefreshDoters) {
      dispatch(fetchConnectedAccountByProvider('doters'));
      if (isSearchResultsPath) hasFetchedDotersOnSearch.current = true;
    }

    // This effect will be done just if doters is enabled and the unified account is authenticated (user?.id)
  }, [accountEnabled, dispatch, features.DOTERS_ENABLED, pathname, user?.id]);

  return (
    <ReserhubAuthContext.Provider
      value={{
        user,
        nextPurchase,
        isNextPurchaseLoading,
        isNextPurchaseFetched,
        isUserLoading,
        logout,
        loginWithDoters,
        isUserFetched,
        enabled: accountEnabled,
        loginUrl: account?.loginUrl,
        homeUrl: account?.homeUrl,
        connectedAccounts,
        isConnectedAccountsFetched,
        isConnectedAccountsLoading,
        clearAllReserhubData: () => dispatch(clearAllReserhubData()),
      }}
    >
      {children}
      {showDotersUnavailableDialog ? <ReserhubDotersUnavailableDialog /> : null}
    </ReserhubAuthContext.Provider>
  );
}

ReserhubAccountProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * Hook to use the ReserhubAuthContext
 * @returns {Object}
 */
export function useReserhubAccount() {
  const context = useContext(ReserhubAuthContext);
  if (!context) {
    throw new Error('useReserhubAccount must be used within a ReserhubAccountProvider');
  }
  return context;
}

/**
 * Hook to use the Reserhub next purchase functionality
 * @returns {Object} Object containing next purchase data and loading states
 */
export function useReserhubAccountNextPurchase() {
  const context = useContext(ReserhubAuthContext);
  if (!context) {
    throw new Error('useReserhubAccountNextPurchase must be used within a ReserhubAccountProvider');
  }
  return {
    nextPurchase: context.nextPurchase,
    isNextPurchaseLoading: context.isNextPurchaseLoading,
    isNextPurchaseFetched: context.isNextPurchaseFetched,
  };
}
